import { isFinite } from "lodash";
import { Language } from "@/i18n/languages";

function decimalSeparator(locale: Language): string {
  switch (locale) {
    case Language.EN:
      return ".";
    case Language.NO:
      return ",";
    case Language.SV:
      return ",";
  }
}

function groupingSeparator(locale: Language): string {
  switch (locale) {
    case Language.EN:
      return ",";
    case Language.NO:
      return " ";
    case Language.SV:
      return " ";
  }
}

export function formatInputString(
  value: number | undefined,
  locale: Language,
  options: {
    isInteger?: boolean;
    useGrouping?: boolean;
    minDecimals?: number;
    maxDecimals?: number;
  } = {},
): string {
  if (value === undefined) {
    return "";
  }

  const formatted = value.toLocaleString(locale.valueOf(), {
    useGrouping: options.useGrouping,
    minimumFractionDigits: options.minDecimals ?? 0,
    maximumFractionDigits: options.isInteger ? 0 : (options.maxDecimals ?? 20),
  });

  return formatted.replace(/\u00a0/g, " ");
}

export function isNegativeNumber(value: string) {
  const normalized = value
    .trim()
    .replace(/[\u2014\u2011\u2013\u002D\u058A\u05BE\u1806\u2010-\u2015\u2E3A\u2212]/g, "-");
  return normalized.trim().startsWith("-");
}

export function filterInputString(
  value: string,
  locale: Language,
  options: { maxDecimals?: number; useGrouping?: boolean; isNegative?: boolean } = {},
): string {
  const numPartRegex = options.useGrouping
    ? new RegExp(`[^\\d${groupingSeparator(locale)}]`)
    : /\D/g;
  const decPartRegex = /\D/g;

  const minus = options.isNegative === true && isNegativeNumber(value) ? "-" : "";
  const parts = value.split(decimalSeparator(locale));
  const num = parts[0].replace(numPartRegex, "");
  const dec =
    options.maxDecimals === 0 || parts.length < 2
      ? null
      : parts[1].replace(decPartRegex, "").substring(0, options.maxDecimals);

  if (dec === null) {
    return `${minus}${num}`;
  }
  return `${minus}${num}${decimalSeparator(locale)}${dec}`;
}

export function parseInputString(value: string, locale: Language): number | undefined {
  if (!value.trim()) {
    return undefined;
  }
  const ds = decimalSeparator(locale);
  const pattern = new RegExp(`[^\\d\\${ds}]`, "g");
  const cleaned = value.replace(pattern, "").replace(ds, ".");
  const parsed = Number(cleaned);
  if (isFinite(parsed)) {
    return isNegativeNumber(value) ? -parsed : parsed;
  }
}
